import { Routes } from '@angular/router';
import { authGuard, domainAdminGuard, firstSiteRedirectGuard, onboardingGuard } from '@privaci/core';
import { AUTH_ROUTES } from './features/auth/auth.routes';
import { agencyAdminGuard } from './features/agency/agency-admin.guard';
import { agencyHomeRedirectGuard, agencySiteRedirectGuard } from './features/agency/agency-home-redirect.guard';
import { environment } from '../environments/environment';

/** The per-site pages both shells share: `/app/sites/:siteId/...` and `/app/agency/sites/:siteId/...`. */
const SITE_PAGE_ROUTES: Routes = [
  { path: 'consent', loadComponent: () => import('./features/dashboard/consent-page/consent-page.component') },
  { path: 'scan', loadComponent: () => import('./features/dashboard/scan-page/scan-page.component') },
  { path: 'banner', loadComponent: () => import('./features/dashboard/banner-page/banner-page.component') },
  { path: 'pages', loadComponent: () => import('./features/dashboard/pages-page/pages-page.component') },
  {
    path: 'cookie-declaration',
    loadComponent: () => import('./features/dashboard/cookie-declaration-page/cookie-declaration-page.component'),
  },
  { path: 'policy', loadComponent: () => import('./features/dashboard/policy-page/policy-page.component') },
  { path: 'team', loadComponent: () => import('./features/dashboard/team-page/team-page.component') },
  { path: 'settings', loadComponent: () => import('./features/dashboard/settings-page/settings-page.component') },
  { path: '**', loadComponent: () => import('./features/not-found/not-found.component') },
];

export const routes: Routes = [
  {
    // Dev-only harness for @privaci/ui (US-C2-1 scaffold baseline). Not linked from app nav and
    // not shipped to production: `canMatch` rejects it whenever environment.production is true, so
    // a production URL falls through to the auth redirect instead of exposing a full primitive
    // inventory (buttons, dialogs, shell chrome) to anonymous visitors.
    path: 'showcase',
    canMatch: [() => !environment.production],
    loadComponent: () => import('./features/showcase/showcase-page.component'),
  },
  {
    path: 'auth',
    children: AUTH_ROUTES,
  },
  {
    // Sibling of `path: 'app'`, listed before it, so it resolves on its own authGuard without
    // ever reaching the onboardingGuard on the /app block below (FEAT-D2) — a zero-site user
    // landing here would otherwise bounce forever between the two guards' redirects.
    path: 'app/onboarding',
    canActivate: [authGuard],
    loadComponent: () =>
      import('./features/onboarding/onboarding-wizard/onboarding-wizard.component'),
  },
  {
    path: 'app',
    canActivate: [authGuard, onboardingGuard],
    children: [
      {
        // An agency admin lands on the Portfolio; everyone else falls through to the first-site
        // redirect. Order matters: the agency guard short-circuits with a UrlTree before the
        // individual redirect runs.
        path: '',
        canActivate: [agencyHomeRedirectGuard, firstSiteRedirectGuard],
        children: [],
      },
      {
        path: 'sites/:siteId',
        canActivate: [agencySiteRedirectGuard],
        loadComponent: () => import('./features/app-shell/app-shell.component'),
        children: [
          { path: '', redirectTo: 'overview', pathMatch: 'full' },
          {
            path: 'overview',
            loadComponent: () => import('./features/dashboard/overview-page/overview-page.component'),
          },
          {
            // Per-site: a subscription is keyed on the site and priced by the site's page count, so
            // Billing lives under the site context and reads the site's own band + invoices.
            path: 'billing',
            loadComponent: () => import('./features/billing/billing-page.component'),
          },
          ...SITE_PAGE_ROUTES,
        ],
      },
      {
        // Account-scoped, and named for what it is. It used to be `/app/settings`, which collided
        // with the per-site `/app/sites/:siteId/settings` — both ended in the same segment, so the
        // shell needed a special case to title them apart (FEAT-D10).
        path: 'account',
        loadComponent: () => import('./features/app-shell/app-shell.component'),
        children: [
          {
            path: '',
            loadComponent: () => import('./features/account/account-page.component'),
          },
        ],
      },
      {
        path: 'admin',
        loadComponent: () => import('./features/app-shell/app-shell.component'),
        children: [
          {
            path: '',
            canActivate: [domainAdminGuard],
            children: [],
          },
        ],
      },
      {
        // Agency-wide, so it sits alongside 'account' rather than under 'sites/:siteId' —
        // tenantId is resolved server-side, never a route param. The whole section is gated on the
        // parent; a denied caller is redirected to the in-app access-denied screen by the guard. The
        // agency portal has its own shell (identity + Portfolio/Billing/Team/Transfers/Settings nav),
        // built to Figma board 243:1826.
        path: 'agency',
        canActivate: [agencyAdminGuard],
        loadComponent: () => import('./features/agency/agency-shell.component'),
        children: [
          { path: '', pathMatch: 'full', redirectTo: 'portfolio' },
          { path: 'portfolio', loadComponent: () => import('./features/agency/agency-portfolio-page.component') },
          { path: 'billing', loadComponent: () => import('./features/agency/agency-billing-page.component') },
          { path: 'team', loadComponent: () => import('./features/agency/agency-team-page.component') },
          { path: 'transfers', loadComponent: () => import('./features/agency/agency-transfers-page.component') },
          { path: 'settings', loadComponent: () => import('./features/agency/agency-settings-page.component') },
          {
            // Per-site drill-down inside the agency shell (Figma 245:*). Reuses the per-site dashboard
            // components; the agency shell switches to the site's nav while a :siteId is in the URL.
            path: 'sites/:siteId',
            children: [
              { path: '', redirectTo: 'overview', pathMatch: 'full' },
              { path: 'overview', loadComponent: () => import('./features/agency/agency-site-overview-page.component') },
              // Billing is agency-wide; shared pages link to '../billing', so land them there.
              { path: 'billing', redirectTo: '/app/agency/billing' },
              ...SITE_PAGE_ROUTES,
            ],
          },
        ],
      },
      {
        // domainAdminGuard's redirect target for non-admins — an in-shell "access denied" card,
        // never a bounce out of the shell (US-C3-2).
        path: 'access-denied',
        loadComponent: () => import('./features/app-shell/app-shell.component'),
        children: [
          {
            path: '',
            loadComponent: () => import('./features/access-denied/access-denied-page.component'),
          },
        ],
      },
      {
        // Catch unknown /app/** routes — show 404 inside the app shell so the
        // user stays authenticated rather than being redirected to login.
        path: '**',
        loadComponent: () => import('./features/app-shell/app-shell.component'),
        children: [
          { path: '', loadComponent: () => import('./features/not-found/not-found.component') },
        ],
      },
    ],
  },
  { path: '', redirectTo: 'auth/login', pathMatch: 'full' },
  { path: '**', redirectTo: 'auth/login' },
];
